import { classInstance } from '@/shared/helpers/helpers';
import { FormMessage } from '@/shared/form/form-message/form-message';
import { Callbacks, Form } from '@/shared/form/form';
import { FormCode } from '@/shared/form/form-element/form-code/form-code';
import { Timer } from '@/shared/common/timer/timer';

/** @inheritdoc */
export interface CodeFormCallbacks extends Callbacks {
	/**
	 * Коллбэк, выполняющийся при нажатии на «Выслать код повторно». Возвращает Promise.
	 * @category 3 Form
	 * @return {Promise<CallbackResult>} Отдаёт объект с успешным статусом и, опционально, дополнительными данными
	 * @throws {Promise<CallbackError>} Отдаёт объект с неудачным статусом, списком ошибок и, опционально, дополнительными данными
	 */
	onResend?: (formData: FormData) => Promise<object>;
}

/**
 * @desc Форма sms-кода
 * @category 6 Авторизация
 * @example
 * window.app.loginTelCode = new CodeForm(document.querySelector('#login-tel-code'));
 * @constructor
 * @param {HTMLFormElement} element HTML-элемент <form>
 * @param {Callbacks} callbacks объект типа Callbacks
 */

export class CodeForm {
	private readonly element: HTMLFormElement;
	private readonly resendClass: string = 'code-form--resend';
	private inputElement: HTMLElement;
	private input: FormCode;
	private messageElement: HTMLElement;
	private message: FormMessage;
	private timerElement: HTMLSpanElement;
	private timer: Timer;
	private resendButton: HTMLButtonElement;
	private telButton: HTMLButtonElement;
	private complete: boolean = false;
	private form: Form;
	private callbacks: CodeFormCallbacks;
	private timerDuration: number = 45000;
	private telInput: HTMLInputElement;

	/**
	 * Параметры таймера
	 * @returns {object}
	 */
	private getTimerSettings = (): object => ({
		count: Date.now() + this.timerDuration,
		format: (data) => `${data.minutes.value}:${data.seconds.value}`,
	});

	constructor(element: HTMLFormElement, callbacks?: CodeFormCallbacks) {
		if (!element) return;
		this.element = element;
		this.callbacks = callbacks;
		this.init();
	}

	/**
	 * Устанавливает длительность таймера.
	 * @param {number} duration длительность в миллисекундах.
	 */
	public setTimerDuration = (duration: number): void => {
		this.timerDuration = duration;
	};

	/**
	 * Скрывает таймер, показывает кнопку.
	 */
	public setResend = (): void => this.element.classList.add(this.resendClass);

	/**
	 * Показывает таймер, скрывает кнопку.
	 */
	public removeResend = (): void => this.element.classList.remove(this.resendClass);

	/**
	 * Запускает таймер. Длительность по умолчанию — 45 секунд, можно изменить методом setTimerDuration.
	 */
	public setTimer = (): void => {
		this.timer?.stop();
		this.timer = new Timer(this.timerElement, this.getTimerSettings());
		this.timer.start();
	};

	/**
	 * Устанавливает номер, на которы был отправлен код.
	 * @param {string} number
	 */
	private setNumberInput = (number: string): void => {
		this.telInput.value = number;
	};

	private setNumberText = (number: string): void => {
		this.telButton.innerText = number;
	};

	public setNumber = (number: string) => {
		this.setNumberInput(number);
		this.setNumberText(number);
	};

	/**
	 * @desc Добавление коллбэков после создания формы.
	 * @param {Callbacks} callbacks объект типа CodeFormCallbacks.
	 * @example
	 * const myForm = app.classInstance.get(document.querySelector('.js-code-form'));
	 * myForm.codeForm.addCallbacks({ onSubmit });
	 */
	public addCallbacks = (callbacks: Callbacks): void => {
		this.callbacks = { ...this.callbacks, ...callbacks };
		this.form.addCallbacks(this.callbacks);
	};

	/**
	 * Обработчик завершения таймера. Переводит форму в состояние, когда возможна повторная отправка — таймер скрыт, кнопка показана.
	 */
	private handleTimerComplete = (): void => {
		this.timer.destroy();
		this.setResend();
	};

	/**
	 * Устанавливает состояние отправленного кода — таймер показан, кнопка скрыта
	 */
	private resend = (): void => {
		this.setTimer();
		this.removeResend();
	};

	/**
	 * Обработчик клика по кнопке «Выслать код повторно». Вызывает коллбэк onResend, если он есть, и в зависимости от его результата:
	 *
	 * * при успехе сбрасывает до состояния отправленного кода;
	 * * при ошибке отображает полученные сообщения.
	 *
	 * Дальнейшая логика привязывается к событию formSubmit..
	 */
	private handleResendClick = (): void => {
		if (this.callbacks.onResend) {
			this.callbacks
				.onResend(new FormData(this.element))
				.then(
					(result) => ((result as any).json ? (result as Response).json() : result),
					(error) => ((error as any)?.json ? (error as Response).json() : error),
				)
				.then((result) => {
					if (result.status === 'success') this.resend();
					else this.form.setMessage(result.errors.map((err) => err.message).join('<br>'), 'error');
				});
		} else this.resend();
	};

	/**
	 * Обработчик полного заполнения формы. Содержит защиту от повторной отправки.
	 */
	private handleInputComplete = (): void => {
		if (this.complete) return;
		this.complete = true;
		this.element.dispatchEvent(new Event('submit'));
	};

	/**
	 * Обработчик изменения полей.
	 */
	private handleInputChange = (): void => {
		if (!this.complete) return;
		this.complete = this.input.validate({ noRender: true });
	};

	/**
	 * Инициализация экземпляра:
	 * * элементы, не создаваемые напрямую из аргументов конструктора;
	 * * обработчики событий.
	 */
	public init = (): void => {
		this.inputElement = this.element.querySelector('.code-form__input input');
		this.messageElement = this.element.querySelector('.code-form__error');
		this.timerElement = this.element.querySelector('.code-form__timer');
		this.resendButton = this.element.querySelector('.code-form__resend-button');
		this.telButton = this.element.querySelector('.code-form__tel');
		this.telInput = this.element.querySelector('.code-form__tel-input');
		this.input = classInstance.get(this.inputElement).formElement;
		this.message = new FormMessage(this.messageElement);
		this.timerElement.addEventListener('timerComplete', this.handleTimerComplete);
		this.resendButton.addEventListener('click', this.handleResendClick);
		this.inputElement.addEventListener('elementChange', this.handleInputChange);
		this.inputElement.addEventListener('elementComplete', this.handleInputComplete);
		this.form = classInstance.get(this.element).form;
		this.form.addCallbacks(this.callbacks);
		classInstance.set(this.element, { codeForm: this });
	};

	/**
	 * Уничтожение экземпляра:
	 * * обнуление элементов;
	 * * удаление обработчиков.
	 */
	public destroy = (): void => {
		this.timerElement.removeEventListener('timerComplete', this.handleTimerComplete);
		this.resendButton.removeEventListener('click', this.handleResendClick);
		this.inputElement.removeEventListener('elementChange', this.handleInputChange);
		this.inputElement.removeEventListener('elementComplete', this.handleInputComplete);
		this.inputElement = null;
		this.messageElement = null;
		this.timerElement = null;
		this.resendButton = null;
		this.telButton = null;
		this.telInput = null;
		this.input = null;
		this.message = null;
		classInstance.del(this.element, 'codeForm');
	};
}
